import {
  type ConfirmAppointmentsFormValues,
  type ParticularsFormValues,
  determinePscStatus,
} from "helpers/forms/particularsTypes";
import { getShareholders } from "helpers/forms/shareholders";
import { CompanyStore } from "services/company/companyStore";
import type { CompanyProductsStore } from "services/companyProducts/companyProductsStore";
import { CompanyProduct } from "services/companyProducts/companyProductsTypes";
import { DirectorStore } from "services/director/directorStore";
import { DirectorPositionStore } from "services/directorPosition/directorPositionStore";
import { FeatureStore } from "services/feature/featureStore";
import { ParticularsEntity } from "services/questions/gb/particularsConstants";
import { FormError } from "types/errors";

export class ParticularsStepStore {
  private readonly _directorStore: DirectorStore;
  private readonly _companyStore: CompanyStore;
  private readonly _companyProductStore: CompanyProductsStore;
  private readonly _directorPositionsStore: DirectorPositionStore;
  private readonly _featureStore: FeatureStore;

  constructor(
    directorStore: DirectorStore,
    companyProductStore: CompanyProductsStore,
    companyStore: CompanyStore,
    directorPositionsStore: DirectorPositionStore,
    featureStore: FeatureStore,
  ) {
    this._directorStore = directorStore;
    this._companyProductStore = companyProductStore;
    this._companyStore = companyStore;
    this._directorPositionsStore = directorPositionsStore;
    this._featureStore = featureStore;
  }

  async updateMainParticulars(
    formValues: ParticularsFormValues | ConfirmAppointmentsFormValues,
    entity: ParticularsEntity,
  ): Promise<void> {
    if (entity === ParticularsEntity.Particulars) {
      const pf = formValues as ParticularsFormValues;
      const { totalShares, totalCapital, directors: directorShares } = pf || {};
      if (!totalShares || !totalCapital || !directorShares) {
        throw new FormError("Something went wrong");
      }
      await this.updateShares(pf);
      return;
    }

    if (entity === ParticularsEntity.ConfirmAppointments) {
      await this.updateConfirmAppointments(formValues as ConfirmAppointmentsFormValues);
      return;
    }

    if (entity === ParticularsEntity.SharesManagement) {
      await this._updateSharesManagement(formValues as ParticularsFormValues);
    }
  }

  private async _updateSharesManagement(formValues: ParticularsFormValues): Promise<void> {
    formValues.sharesManagement
      ? await this._companyProductStore.addProduct(CompanyProduct.SharesManagement)
      : await this._companyProductStore.deleteProduct(CompanyProduct.SharesManagement);
  }

  async updateShares(formValues: ParticularsFormValues): Promise<void> {
    const { totalShares, totalCapital, directors: directorShares } = formValues || {};

    if (!totalShares || !totalCapital || !directorShares) {
      throw new FormError("Something went wrong");
    }

    await this._companyStore.updateShares(totalShares, totalCapital);

    const totalSharesNum = parseInt(totalShares);

    // Mirror the UI's sole-shareholder detection (see Shares.tsx / useShareholders): the upgraded
    // sole screen auto-confirms PSC inline and skips the ConfirmAppointments step, so when it is
    // active we must persist PSC here. Using the unfiltered director count would diverge from the
    // UI whenever a director-only person exists alongside a single shareholder, silently dropping
    // the sole owner's PSC. Gated on the same feature flag so non-upgraded flows are unchanged.
    const shareholders = getShareholders(
      this._directorStore.directors,
      this._directorStore.mainDirector?.id,
      this._directorPositionsStore,
    );
    const isUpgradedSoleShareholder =
      this._featureStore.hasFeature("formationUpgradedShares") && shareholders.length === 1;
    const isMultiShareholder = !isUpgradedSoleShareholder && this._directorStore.directors.length > 1;

    for (const director of this._directorStore.directors) {
      const value = directorShares[director.id];
      const sharesNum = value ? parseInt(value) : 0;
      await this._directorStore.updateShares(director.id, value ? parseInt(value) : undefined);

      if (!isMultiShareholder) {
        // Single shareholder: auto-determine PSC
        const natureOfControl = formValues.directorsNatureOfControl?.[director.id] ?? [];
        const psc = determinePscStatus(sharesNum, totalSharesNum, natureOfControl);

        await this._directorStore.updateSignificantControl(
          director.id,
          psc.isSignificant,
          psc.hasRightToAppoint,
          psc.hasSignificantControl,
        );
      }
    }
  }

  async updateConfirmAppointments(formValues: ConfirmAppointmentsFormValues): Promise<void> {
    const { directorsNatureOfControl } = formValues;
    const totalSharesNum = this._companyStore.company?.totalSharesNum ?? 0;

    for (const director of this._directorStore.directors) {
      const sharesNum = director.ownsSharesNum ?? 0;
      const natureOfControl = directorsNatureOfControl?.[director.id] ?? [];
      const psc = determinePscStatus(sharesNum, totalSharesNum, natureOfControl);

      await this._directorStore.updateSignificantControl(
        director.id,
        psc.isSignificant,
        psc.hasRightToAppoint,
        psc.hasSignificantControl,
      );
    }
  }
}
